import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import FindLeagueHelp from "./booth/FindLeagueHelp";
import "./booth/Modal.css";
import "../styles/addLeagueModal.css";
import { useUser } from "../context/user";
import { useLeagues } from "../context/leagues";

const EASE = [0.4, 0, 0.2, 1];
const FADE_TRANSITION = { duration: 0.17, ease: EASE }; // mirrors --booth-motion-fade

const STEPS = ["Find your league", "Claim your team"];

const PLATFORM_LOGOS = {
  espn: { src: "/fantasy-football-espn.png", label: "ESPN" },
  sleeper: { src: "/sleeper-logo.png", label: "Sleeper" },
};

function AddLeagueModal({ open, onClose }) {
  const user = useUser();
  const leagues = useLeagues();
  const [leagueData, setLeagueData] = useState({});
  const [leagueId, setLeagueId] = useState("");
  const [leagueIdErr, setLeagueIdErr] = useState(false);
  const [leagueIdErrMessage, setLeagueIdErrMessage] = useState(null);
  const [platform, setPlatform] = useState("espn");
  const [activeStep, setActiveStep] = useState(0);
  const [ownedTeam, setOwnedTeam] = useState(null);
  const [loadingLeague, setLoadingLeague] = useState(false);
  const [addingLeague, setAddingLeague] = useState(false);
  const [leagueAddErr, setLeagueAddErr] = useState(null);
  const overlayRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the modal's content out of the tab order while closed — aria-hidden
  // alone doesn't stop keyboard users tabbing into a visually hidden dialog.
  useEffect(() => {
    if (overlayRef.current) overlayRef.current.inert = !open;
  }, [open]);

  // Move focus into the dialog when it opens, so keyboard/screen-reader
  // users land somewhere predictable instead of staying on the trigger
  // behind a now-hidden page. Deferred a frame so it runs after the
  // triggering click's own native focus-the-button behavior, rather than
  // racing it.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => panelRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const findLeague = async (event) => {
    event.preventDefault();
    setLoadingLeague(true);
    setLeagueIdErr(false);
    setLeagueIdErrMessage(null);

    try {
      const resp = await leagues.add(leagueId, user.current.email, platform);
      if (resp.error) {
        setLeagueIdErr(true);
        setLeagueIdErrMessage(
          resp.error === "LEAGUE_PRIVATE"
            ? "This league is private — set it to Public in your ESPN League Settings, then try again."
            : "Invalid ID"
        );
      } else {
        setLeagueIdErr(false);
        setLeagueData(resp.status);
        // The input may have been a pasted league URL; adopt the canonical
        // numeric ID the backend resolved it to so step 2 always persists
        // a clean ID, not whatever the user happened to paste.
        setLeagueId(String(resp.status.id));
        setActiveStep(1);
      }
    } catch (error) {
      setLeagueIdErr(true);
      setLeagueIdErrMessage("Invalid ID");
    } finally {
      setLoadingLeague(false);
    }
  };

  const backStep = () => {
    if (activeStep > 0) {
      setLeagueAddErr(null);
      setActiveStep(activeStep - 1);
    }
  };

  const handleClose = () => {
    setLeagueAddErr(null);
    onClose();
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const addLeagueToDatabase = async () => {
    setAddingLeague(true);
    setLeagueAddErr(null);
    try {
      const result = await leagues.addLeague(leagueId, leagueData, ownedTeam, platform);
      if (!result.success) {
        if (result.error === "LEAGUE_ALREADY_EXISTS") {
          setLeagueAddErr("This league is already on your dashboard.");
        } else {
          setLeagueAddErr("Failed to add league. Please try again.");
        }
        return;
      }
      handleClose();
    } catch (error) {
      setLeagueAddErr("Failed to add league. Please try again.");
    } finally {
      setAddingLeague(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      className={`booth-modal-overlay ${open ? "booth-modal-overlay--open" : ""}`}
      onClick={handleOverlayClick}
      aria-hidden={!open}
    >
      <div
        ref={panelRef}
        className="booth-modal-panel booth-add-league-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Add a league"
        tabIndex={-1}
      >
        <button
          type="button"
          className="booth-modal-close"
          aria-label="Close"
          onClick={handleClose}
        >
          ×
        </button>

        <h2 className="booth-modal-title">Add a league</h2>

        <div className="booth-steps">
          {STEPS.map((label, index) => (
            <div className="booth-step-group" key={label}>
              <div
                className={`booth-step ${activeStep === index ? "booth-step--active" : ""} ${
                  activeStep > index ? "booth-step--done" : ""
                }`}
              >
                <span className="booth-step-num">{String(index + 1).padStart(2, "0")}</span>
                <span className="booth-step-label">{label}</span>
              </div>
              {index < STEPS.length - 1 && <span className="booth-step-rule" aria-hidden="true" />}
            </div>
          ))}
        </div>

        <MotionConfig transition={FADE_TRANSITION} reducedMotion="user">
          <AnimatePresence mode="wait" initial={false}>
            {activeStep === 0 && (
              <motion.div
                key="step-find"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div
                  className="booth-platform-toggle"
                  role="radiogroup"
                  aria-label="Platform"
                >
                  {Object.entries(PLATFORM_LOGOS).map(([value, { src, label }]) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={platform === value}
                      className={`booth-platform-btn ${
                        platform === value ? "booth-platform-btn--active" : ""
                      }`}
                      onClick={() => setPlatform(value)}
                    >
                      <img src={src} alt="" className="booth-platform-logo" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>

                <form onSubmit={findLeague} className="booth-modal-form" noValidate>
                  <label className="booth-field">
                    <span className="booth-field-label">League link or ID</span>
                    <input
                      id="league-id-input"
                      type="text"
                      placeholder="Paste your league link, or enter the ID"
                      className={`booth-field-input ${leagueIdErr ? "booth-field-input--error" : ""}`}
                      value={leagueId}
                      onChange={(event) => setLeagueId(event.target.value)}
                    />
                    {leagueIdErr && (
                      <span className="booth-field-error">{leagueIdErrMessage}</span>
                    )}
                  </label>
                  <button
                    type="submit"
                    className="booth-btn booth-btn--primary booth-modal-submit"
                    disabled={loadingLeague}
                  >
                    {loadingLeague ? "Checking…" : (
                      <>
                        Continue <span aria-hidden="true">→</span>
                      </>
                    )}
                  </button>
                </form>

                <details className="booth-disclosure" open>
                  <summary className="booth-disclosure-summary">
                    Where to find your league link
                    <span className="booth-find-help-chevron" aria-hidden="true">⌄</span>
                  </summary>
                  <div className="booth-disclosure-body">
                    <FindLeagueHelp platform={platform} />
                  </div>
                </details>
              </motion.div>
            )}

            {activeStep === 1 && (
              <motion.div
                key="step-claim"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {leagueAddErr && <p className="booth-modal-error">{leagueAddErr}</p>}

                <div className="booth-league-summary-row">
                  <span className="booth-league-summary-text">
                    <span className="booth-league-summary-name">
                      {leagueData.leagueName}
                    </span>
                    <span className="booth-league-summary-meta">
                      {platform === "sleeper" ? "Sleeper" : "ESPN"},{" "}
                      {leagueData.teams?.length ?? 0} teams, league {leagueId}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="booth-link-underline"
                    onClick={backStep}
                  >
                    Change
                  </button>
                </div>

                <fieldset className="booth-team-fieldset">
                  <legend className="booth-team-legend">Which team is yours?</legend>
                  <span className="booth-team-dek">
                    Your dispatch comes to you first, and the desk knows whose
                    side it's writing from.
                  </span>
                  <div className="booth-team-list">
                    {leagueData.teams?.map((team, index) => (
                      <label className="booth-team-row" key={team.id ?? index}>
                        <input
                          type="radio"
                          name="team"
                          className="booth-team-row-input"
                          checked={ownedTeam === team}
                          onChange={() => setOwnedTeam(team)}
                        />
                        <span className="booth-team-row-radio" aria-hidden="true">
                          {ownedTeam === team && (
                            <span className="booth-team-row-dot" />
                          )}
                        </span>
                        <span className="booth-team-row-name tname">
                          {team.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="booth-modal-actions booth-modal-actions--split">
                  <button
                    type="button"
                    className="booth-btn booth-btn--secondary"
                    onClick={backStep}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="booth-btn booth-btn--primary"
                    disabled={ownedTeam === null || addingLeague}
                    onClick={addLeagueToDatabase}
                  >
                    {addingLeague ? "Adding league…" : "Add league"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </MotionConfig>
      </div>
    </div>
  );
}

export default AddLeagueModal;
